import {
  Document, Page, StyleSheet, Text, View,
} from "@react-pdf/renderer";
import moment from "moment";
import React from "react";
import { formatCurrency } from "../../../utils/currency";

const BRAND = "#28B0C4";
const DARK = "#1E293B";
const MID = "#475569";
const LIGHT = "#94A3B8";
const BORDER = "#E2E8F0";
const BG = "#F8FAFC";
const WHITE = "#FFFFFF";
const RED = "#EF4444";
const GREEN = "#10B981";

const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: MID,
    backgroundColor: WHITE,
  },

  // ── Top bar ──
  topBar: {
    backgroundColor: BRAND,
    height: 6,
    marginBottom: 0,
  },

  // ── Header ──
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 40,
    paddingTop: 28,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  logoContainer: {
    flexDirection: "column",
  },
  businessName: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: BRAND,
    marginBottom: 4,
  },
  businessInfo: {
    fontSize: 8,
    color: MID,
    marginBottom: 2,
  },
  invoiceTitleContainer: {
    alignItems: "flex-end",
  },
  invoiceTitle: {
    fontSize: 28,
    fontFamily: "Helvetica-Bold",
    color: DARK,
    textTransform: "uppercase",
    letterSpacing: 3,
    marginBottom: 10,
  },
  invoiceMetaItem: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 4,
    gap: 8,
  },
  metaLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: LIGHT,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  metaValue: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: DARK,
  },

  // ── Addresses ──
  addressSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginHorizontal: 40,
    marginTop: 24,
    marginBottom: 28,
    gap: 14,
  },
  addressBox: {
    width: "47%",
    backgroundColor: BG,
    borderRadius: 6,
    padding: 14,
    borderLeftWidth: 3,
    borderLeftColor: BRAND,
  },
  addressHeader: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: BRAND,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 7,
  },
  addressText: {
    fontSize: 9,
    color: MID,
    marginBottom: 2,
    lineHeight: 1.4,
  },
  addressName: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: DARK,
    marginBottom: 3,
  },

  // ── Table ──
  table: {
    marginHorizontal: 40,
    marginTop: 0,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: DARK,
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  tableRowAlt: {
    backgroundColor: BG,
  },
  columnHeader: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: WHITE,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  columnText: {
    fontSize: 9,
    color: DARK,
  },
  colDesc: { width: "30%" },
  colQty: { width: "8%", textAlign: "center" },
  colRate: { width: "14%", textAlign: "right" },
  colDisc: { width: "14%", textAlign: "right" },
  colTax: { width: "14%", textAlign: "right" },
  colAmount: { width: "20%", textAlign: "right" },

  // ── Summary ──
  summaryContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginHorizontal: 40,
  },
  summaryBox: {
    width: "42%",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  summaryLabel: {
    fontSize: 9,
    color: MID,
  },
  summaryValue: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: DARK,
  },
  grandTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
    padding: 12,
    backgroundColor: BRAND,
    borderRadius: 6,
  },
  grandTotalLabel: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: WHITE,
  },
  grandTotalValue: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    color: WHITE,
  },

  // ── Footer ──
  footer: {
    marginTop: 30,
    marginHorizontal: 40,
    flexDirection: "row",
    gap: 16,
  },
  notesSection: {
    flex: 1,
  },
  footerHeader: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: BRAND,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 5,
  },
  footerText: {
    fontSize: 8,
    color: MID,
    lineHeight: 1.5,
    backgroundColor: BG,
    padding: 8,
    borderRadius: 4,
  },

  // ── Bottom bar ──
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: DARK,
    paddingVertical: 8,
    paddingHorizontal: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  bottomText: {
    fontSize: 7,
    color: LIGHT,
  },
  bottomBrand: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: WHITE,
  },
});

const formatPhone = (phone) => {
  if (!phone) return "";
  if (typeof phone === 'string') return phone;
  return `${phone.countryCode || ''} ${phone.number || ''}`.trim();
};

export default function InvoicePdf({ data }) {
  const currencyCode = data?.currency?.code || data?.currency || "CAD";

  return (
    <Document title={`Invoice #${data?._id}`}>
      <Page size="A4" style={styles.page}>

        {/* ── Top accent bar ── */}
        <View style={styles.topBar} />

        {/* ── Header — same data as original ── */}
        <View style={styles.header}>
          <View style={styles.logoContainer}>
            <Text style={styles.businessName}>{data?.business?.name || "Your Business"}</Text>
            <Text style={styles.businessInfo}>{data?.business?.contact?.email || ""}</Text>
            <Text style={styles.businessInfo}>{data?.business?.location || ""}</Text>
            <Text style={styles.businessInfo}>
              {[
                data?.business?.addressInfo?.city,
                data?.business?.addressInfo?.state,
                data?.business?.addressInfo?.zipCode
              ].filter(Boolean).join(", ")}
            </Text>
            <Text style={styles.businessInfo}>{data?.business?.addressInfo?.country || ""}</Text>
          </View>
          <View style={styles.invoiceTitleContainer}>
            <Text style={styles.invoiceTitle}>Invoice</Text>
            <View style={styles.invoiceMetaItem}>
              <Text style={styles.metaLabel}>Invoice #:</Text>
              <Text style={styles.metaValue}>{data?._id?.toString().slice(-6).toUpperCase() || "N/A"}</Text>
            </View>
            <View style={styles.invoiceMetaItem}>
              <Text style={styles.metaLabel}>Issued Date:</Text>
              <Text style={styles.metaValue}>{moment(data?.invoiceDate).format("MMM DD, YYYY")}</Text>
            </View>
            <View style={styles.invoiceMetaItem}>
              <Text style={styles.metaLabel}>Due Date:</Text>
              <Text style={styles.metaValue}>{moment(data?.invoiceDueDate).format("MMM DD, YYYY")}</Text>
            </View>
          </View>
        </View>

        {/* ── Addresses — same data as original ── */}
        <View style={styles.addressSection}>
          <View style={styles.addressBox}>
            <Text style={styles.addressHeader}>Bill From</Text>
            <Text style={styles.addressName}>{data?.business?.name || "N/A"}</Text>
            <Text style={styles.addressText}>{data?.business?.contact?.email || ""}</Text>
            <Text style={styles.addressText}>{data?.business?.location || ""}</Text>
            <Text style={styles.addressText}>
                {[
                  data?.business?.addressInfo?.city,
                  data?.business?.addressInfo?.state,
                  data?.business?.addressInfo?.zipCode,
                  data?.business?.addressInfo?.country
                ].filter(Boolean).join(", ")}
            </Text>
          </View>
          <View style={styles.addressBox}>
            <Text style={styles.addressHeader}>Bill To</Text>
            <Text style={styles.addressName}>{data?.customer?.name}</Text>
            <Text style={styles.addressText}>{data?.customer?.email}</Text>
            <Text style={styles.addressText}>
              {typeof data?.customer?.address === 'object'
                ? [
                  data?.customer?.address?.street,
                  data?.customer?.address?.city,
                  data?.customer?.address?.state,
                  data?.customer?.address?.zipCode,
                  data?.customer?.address?.country,
                ].filter(Boolean).join(', ')
                : (data?.customer?.address || '')}
            </Text>
            {data?.customer?.phone && (
              <Text style={styles.addressText}>{formatPhone(data.customer.phone)}</Text>
            )}
          </View>
        </View>

        {/* ── Table — same data as original ── */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.columnHeader, styles.colDesc]}>Description</Text>
            <Text style={[styles.columnHeader, styles.colQty]}>Qty</Text>
            <Text style={[styles.columnHeader, styles.colRate]}>Rate</Text>
            <Text style={[styles.columnHeader, styles.colDisc]}>Disc.</Text>
            <Text style={[styles.columnHeader, styles.colTax]}>Tax</Text>
            <Text style={[styles.columnHeader, styles.colAmount]}>Amount</Text>
          </View>

          {data?.productDetail?.map((item, index) => (
            <View
              key={index}
              style={[styles.tableRow, index % 2 === 1 ? styles.tableRowAlt : {}]}
            >
              <Text style={[styles.columnText, styles.colDesc, { fontFamily: "Helvetica-Bold" }]}>
                {item.productName || "Product"}
              </Text>
              <Text style={[styles.columnText, styles.colQty]}>{item.quantity || 0}</Text>
              <Text style={[styles.columnText, styles.colRate]}>{formatCurrency(item.rate, currencyCode)}</Text>
              <Text style={[styles.columnText, styles.colDisc, { color: RED }]}>
                {item.discountamount > 0 ? `-${formatCurrency(item.discountamount, currencyCode)}` : "—"}
              </Text>
              <Text style={[styles.columnText, styles.colTax, { color: GREEN }]}>
                {item.tax > 0 ? `+${formatCurrency(item.tax, currencyCode)}` : "—"}
              </Text>
              <Text style={[styles.columnText, styles.colAmount, { fontFamily: "Helvetica-Bold", color: BRAND }]}>
                {formatCurrency(item.total, currencyCode)}
              </Text>
            </View>
          ))}
        </View>

        {/* ── Summary — same data as original ── */}
        <View style={styles.summaryContainer}>
          <View style={styles.summaryBox}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Subtotal</Text>
              <Text style={styles.summaryValue}>{formatCurrency(data?.netAmount, currencyCode)}</Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Discount</Text>
              <Text style={[styles.summaryValue, { color: RED }]}>
                -{formatCurrency(data?.invoiceDiscount, currencyCode)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Tax</Text>
              <Text style={[styles.summaryValue, { color: GREEN }]}>
                +{formatCurrency(data?.invoiceTax, currencyCode)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Shipping</Text>
              <Text style={styles.summaryValue}>+{formatCurrency(data?.shippingCharge, currencyCode)}</Text>
            </View>
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Total ({currencyCode})</Text>
              <Text style={styles.grandTotalValue}>{formatCurrency(data?.totalAmount, currencyCode)}</Text>
            </View>
          </View>
        </View>

        {/* ── Footer — same data as original ── */}
        {(data?.notes || data?.terms) && (
          <View style={styles.footer}>
            {data?.notes && (
              <View style={styles.notesSection}>
                <Text style={styles.footerHeader}>Notes</Text>
                <Text style={styles.footerText}>{data?.notes}</Text>
              </View>
            )}
            {data?.terms && (
              <View style={styles.notesSection}>
                <Text style={styles.footerHeader}>Terms & Conditions</Text>
                <Text style={styles.footerText}>{data?.terms}</Text>
              </View>
            )}
          </View>
        )}

        {/* ── Bottom bar ── */}
        <View style={styles.bottomBar} fixed>
          <Text style={styles.bottomText}>
            Generated {moment().format("MMM DD, YYYY")} · Invoice #{data?._id?.toString().slice(-6).toUpperCase() || "N/A"}
          </Text>
          <Text style={styles.bottomBrand}>{data?.business?.name}</Text>
        </View>

      </Page>
    </Document>
  );
}